import React, { useCallback, useMemo } from 'react';
import { Heading, ISelectMiniOption, SelectMini } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useParams } from 'react-router';
import { TemplateManagementRoute } from 'sections/design/template-management/routes';
import { SUPPORTED_LANGUAGES } from 'sections/client-management/ui/pages/client-settings/supported-languages/constants/SupportedLanguagesConstants';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { NameBarElement, NameBarElementItem } from './style';

const getLanguageLabel = (languageId: string, t: (key: string) => string): string => {
  const entry = SUPPORTED_LANGUAGES.find(l => l.id === languageId);
  return entry ? t(entry.name) : languageId;
};

export const RcsTemplateEditorNameBar: React.FC = () => {
  const { t } = useTranslation(['designs', 'supportedLanguages']);
  const { siteId, id: templateId, language: routeLanguage } = useParams<{
    siteId: string;
    id: string;
    language: string;
  }>();
  const history = useHistory();
  const { templateName, selectedLanguage, supportedLanguages } = useRcsTemplateEditorState();
  const dispatch = useRcsTemplateEditorDispatch();

  const displayName = templateName || t('designs:rcsTemplateEditor.defaultName');

  const languageOptions: ISelectMiniOption[] = useMemo(
    () =>
      supportedLanguages.map(lan => ({
        value: lan.language,
        label: getLanguageLabel(lan.language, t),
      })),
    [supportedLanguages, t],
  );

  const selectedValue: ISelectMiniOption | undefined = useMemo(() => {
    const lang = selectedLanguage || routeLanguage;
    const label = getLanguageLabel(lang, t);
    return { value: lang, label };
  }, [selectedLanguage, routeLanguage, t]);

  const onLanguageChange = useCallback(
    (option: ISelectMiniOption) => {
      dispatch({ type: 'SET_SELECTED_LANGUAGE', payload: { selectedLanguage: option.value } });
      history.push(
        generatePath(TemplateManagementRoute.EDIT_RCS, {
          siteId,
          id: templateId,
          language: option.value,
        }),
      );
    },
    [dispatch, history, siteId, templateId],
  );

  return (
    <NameBarElement data-testid="RcsTemplateEditorNameBar">
      <NameBarElementItem>
        <Heading variation="h4">{displayName}</Heading>
      </NameBarElementItem>
      <NameBarElementItem minWidth="110px">
        <SelectMini
          ariaLabel={t('designs:designGallery.templateEditor.nameToolbar.languages.label')}
          inputId="RcsTemplateLanguageID"
          placeholder={t('designs:designGallery.templateEditor.nameToolbar.languages.label')}
          options={languageOptions}
          value={selectedValue}
          onChange={onLanguageChange}
        />
      </NameBarElementItem>
    </NameBarElement>
  );
};
